import { Component, Inject, OnInit } from '@angular/core';
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { KeyValue } from '@angular/common';

import { firstValueFrom } from 'rxjs';

import { IS_NULL_OR_UNDEFINED } from 'cargo-chief-commons';
import { BaseComponent } from '@shared/base/base.component';
import { ProjectDialog, ProjectDialogInputData } from '../project-dialog/project-dialog.component';
import { PROJECT_TYPES, IS_WORKFLOW_PROJECT_TYPE } from '@domain/constants/project-types';
import { NotifyProcuredCarriersDialogComponent } from '../notify-procured-carriers-dialog/notify-procured-carriers-dialog.component';
import { environment } from '@environment/environment';

@Component({
	selector: 'app-project-type-dialog',
	templateUrl: './project-type-dialog.component.html',
	styleUrls: ['./project-type-dialog.component.scss'],
})
export class ProjectTypeDialogComponent extends BaseComponent implements OnInit {
	projectTypes: any = PROJECT_TYPES;
	availableProjectTypes: any = {};
	projectTypeDetails: FormGroup = new FormGroup({
		type: new FormControl('', [Validators.required]),
	});

	async ngOnInit() {
		// Capacity Search: show only Procurement + Workflow (maps to WATERFALL), no default
		if (this.data?.showProjectTypeSelectionDialog) {
			this.availableProjectTypes = {
				PROCUREMENT: {
					label: 'Procurement',
					value: PROJECT_TYPES.PROCUREMENT.value,
					sortOrder: 0,
				},
				WORKFLOW: {
					label: 'Workflow',
					value: PROJECT_TYPES.WATERFALL.value,
					sortOrder: 1,
				},
			};
			this.projectTypeDetails.patchValue({ type: '' });
			return;
		}

		// Filter available project types based on data.allowedTypes
		if (this.data?.allowedTypes && Array.isArray(this.data.allowedTypes)) {
			// Initialize to empty object first
			this.availableProjectTypes = {};
			
			// De-duplicate allowedTypes and iterate over unique list
			const uniqueAllowedTypes = [...new Set(this.data.allowedTypes)];
			
			// Only add entries that exist in PROJECT_TYPES
			uniqueAllowedTypes.forEach(type => {
				if (PROJECT_TYPES[type]) {
					this.availableProjectTypes[type] = PROJECT_TYPES[type];
				}
			});
			
			// If no valid types found, clear preselected project type
			if (Object.keys(this.availableProjectTypes).length === 0) {
				this.projectTypeDetails.patchValue({
					type: '',
				});
			} else {
				// Set default selection to first available type
				const firstAvailableType = Object.values(this.availableProjectTypes)[0] as any;
				this.projectTypeDetails.patchValue({
					type: firstAvailableType?.value || '',
				});
			}
		} else {
			// Default to all project types
			this.availableProjectTypes = PROJECT_TYPES;
			
			// Set default selection to first available type
			const firstAvailableType = Object.values(this.availableProjectTypes)[0] as any;
			this.projectTypeDetails.patchValue({
				type: firstAvailableType?.value || PROJECT_TYPES.PROCUREMENT.value,
			});
		}
	}

	constructor(
		public dialog: MatDialog,
		public dialogRef: MatDialogRef<ProjectTypeDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ProjectDialogInputData
	) {
		super();
	}

	async onProjectTypeSelection() {
		const projectType = this.projectTypeDetails.get('type').value;
		let notifyProcuredCarriers: boolean = false;
		
		// Only show notify dialog here for non-Workflows pages (to maintain backward compatibility)
		const isFromWorkflows = this.data?.allowedTypes && 
			Array.isArray(this.data.allowedTypes) && 
			this.data.allowedTypes.length === 2 &&
			this.data.allowedTypes.includes('WATERFALL') && 
			this.data.allowedTypes.includes('DEDICATED_LANE');
		
		if (
			!isFromWorkflows &&
			!this.data?.showProjectTypeSelectionDialog &&
			environment.FEATURE_FLAGS.PROCURED_CARRIERS_EMAIL &&
			IS_WORKFLOW_PROJECT_TYPE(projectType)
		) {
			const notifyCarriersDialogResponse = await this.dialog
				.open(NotifyProcuredCarriersDialogComponent, {
					panelClass: 'cargo_chief_dialog_container',
					width: '450px',
					disableClose: true,
				})
				.afterClosed()
				.toPromise();
			if (notifyCarriersDialogResponse) {
				notifyProcuredCarriers = notifyCarriersDialogResponse.notifyProcuredCarriers;
			}
		}
		
		this.createProject(notifyProcuredCarriers);
	}

	createProject(notifyProcuredCarriers: boolean = false): void {
		const data: ProjectDialogInputData = {
			...this.data,
			type: this.projectTypeDetails.get('type').value,
			notifyProcuredCarriers,
		};
		const projectDialogRef = this.dialog.open(ProjectDialog, {
			panelClass: 'cargo_chief_dialog_container',
			width: '950px',
			data,
			disableClose: true,
		});
		this.dialogRef.close(projectDialogRef);
	}

	closeDialog(data: any): void {
		this.dialogRef.close(data);
	}

	sortProjects(a: KeyValue<string, any>, b: KeyValue<string, any>): number {
		return a.value.sortOrder - b.value.sortOrder;
	}

	getDialogTitle(): string {
		// Check if this is the Workflows page by looking at allowedTypes
		const isFromWorkflows = this.data?.allowedTypes && 
			Array.isArray(this.data.allowedTypes) && 
			this.data.allowedTypes.length === 2 &&
			this.data.allowedTypes.includes('WATERFALL') && 
			this.data.allowedTypes.includes('DEDICATED_LANE');
		
		return isFromWorkflows ? 'Select Workflow Type' : 'Select Project Type';
	}
}
